import { Component } from '../Component';
import { ArgSpecDictionary } from '../ComponentUtils';
import { HHDSVideoVimeo, HHDSVideoVimeoTagName } from '../VideoVimeo/VideoVimeo';
import { HHDSVideoEvent } from '../../utils/VideoTypes';
import { HHDSButtonHero } from '../ButtonHero/ButtonHero';

const DEBUG_VERBOSE: boolean = false;
export const HHDSHeroVideoPlayerTagName: string = 'hhds-hero-video-player';
const TAG_NAME: string = HHDSHeroVideoPlayerTagName;
const LOG_PREFIX: string = `[${TAG_NAME}]`;

export const HHDSHeroVideoPlayerAttrNames = {
	src: 'src',
	poster: 'poster',
	showScrollButton: 'show-scroll-button',
	overlayEnabled: 'overlay-enabled',
};

const Attrs = HHDSHeroVideoPlayerAttrNames;

export class HHDSHeroVideoPlayer extends Component {
	private video: HHDSVideoVimeo | null = null;

	constructor() {
		super();
	}

	protected override init(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'init');

		const scrollDownButtonHtml = this.htmlIf(Attrs.showScrollButton, () => {
			return `<hhds-button-hero class="${TAG_NAME}__button" mode="light"></hhds-button-hero>`;
		});
		const overlayHtml = this.htmlIf(Attrs.overlayEnabled, () => {
			return `<div class="${TAG_NAME}__overlay"></div>`;
		});

		const html: string = `
      <div class="${TAG_NAME}">
				<div class="${TAG_NAME}__content">
					${overlayHtml}
					${this.videoHtml}
					${scrollDownButtonHtml}
				</div>
				<div class="${TAG_NAME}__bottom-anchor"></div>
			</div>
    `;

		this.setInnerHtml('', '', html);

		this.video = this.root.querySelector(HHDSVideoVimeoTagName) as HHDSVideoVimeo;

		const overlay = this.element(`.${TAG_NAME}__overlay`);
		if (overlay) {
			overlay.style.display = 'none';
			this.video?.addEventListener(HHDSVideoEvent.coverLoaded, () => {
				overlay.style.display = 'block';
			});
		}

		const scrollButton: HHDSButtonHero = this.element<HHDSButtonHero>('hhds-button-hero');
		const showScrollButton = () => {
			if (scrollButton) scrollButton.style.display = '';
		};
		const hideScrollButton = () => {
			if (scrollButton) scrollButton.style.display = 'none';
		};

		this.video.addEventListener(HHDSVideoEvent.playRequest, () => {
			overlay.style.display = 'none';
			hideScrollButton();
		});
		this.video.addEventListener(HHDSVideoEvent.play, () => {
			overlay.style.display = 'none';
			hideScrollButton();
		});
		this.video.addEventListener(HHDSVideoEvent.pause, () => {
			overlay.style.display = 'none';
			hideScrollButton();
		});
		this.video.addEventListener(HHDSVideoEvent.ended, () => {
			overlay.style.display = 'block';
			showScrollButton();
		});

		const bottomAnchor = this.element(`.${TAG_NAME}__bottom-anchor`) as HTMLElement;
		if (bottomAnchor) {
			const scrollButton = this.element('hhds-button-hero');
			scrollButton?.addEventListener('click', () => {
				const rect = bottomAnchor.getBoundingClientRect();
				const yPos = rect.top + window.scrollY;
				window.scrollTo({ top: yPos, behavior: 'smooth' });
			});
		}

		this.observeSlotChanges(true);
	}

	private get videoHtml(): string {
		const src: string = this.vars.get<string>(HHDSHeroVideoPlayerAttrNames.src);
		const poster: string = this.vars.get<string>(HHDSHeroVideoPlayerAttrNames.poster);
		const posterAttr: string = poster ? `poster="${poster}"` : '';
		const videoHtml: string = `<${HHDSVideoVimeoTagName} use-container="false" src="${src}" ${posterAttr}></${HHDSVideoVimeoTagName}>`;
		return videoHtml;
	}

	protected override destroy(): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, 'destroy');
		this.video?.pause();
	}

	override onAttributeChanged(name: string, _oldValue: string, _newValue: string): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onAttributeChanged: ${name}`);
		this.reinit();
	}

	override onSlotChange(_slot: HTMLSlotElement, _elements: HTMLElement[]): void {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, `onSlotChange`);
	}

	static override argSpecs(): ArgSpecDictionary {
		return ArgSpecs;
	}
}

export const ArgSpecs: ArgSpecDictionary = {
	[Attrs.src]: {
		description: 'The Vimeo URL to play.',
		type: String,
		defaultValue: '',
	},
	[Attrs.poster]: {
		description: 'The poster image to show before the video plays.',
		type: String,
		defaultValue: '',
	},
	[Attrs.showScrollButton]: {
		description: 'Whether to show the scroll button.',
		defaultValue: true,
		type: Boolean,
	},
	[Attrs.overlayEnabled]: {
		description: 'Whether to enable the gradient overlay.',
		defaultValue: true,
		type: Boolean,
	},
};
